import React, { useEffect, useReducer, useState } from 'react';
import {
  Accordion
  , AccordionDetails
  , AccordionSummary
  , Button
  , Card
  , CardActionArea
  , CardContent
  , Container, Dialog
  , DialogActions
  , DialogContent
  , DialogTitle
  , Divider, InputAdornment
  , TextField
  , Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { toast } from 'react-toastify';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import SearchIcon from '@material-ui/icons/Search';
import ViewHeadlineOutlinedIcon from '@material-ui/icons/ViewHeadlineOutlined';
import Lottie from 'react-lottie';

import { i18n } from '../../translate/i18n';
import NotFoundLottie from '../../assets/lottie/notFound.json';
import SearchLottie from '../../assets/lottie/search.json';
import api from '../../services/api';
import toastError from '../../errors/toastError';
import LottieUtils from '../../utils/classes/LottieUtils';

const reducer = (state, action) => {
  if (action.type === 'LOAD_TEMPLATES') {
    const templates = action.payload;
    const newTemplates = [];

    templates.forEach(template => {
      const templateIndex = state.findIndex(t => t.id === template.id);
      if (templateIndex !== -1) state[templateIndex] = template;
      else newTemplates.push(template);
    });

    return [...state, ...newTemplates];
  }

  if (action.type === 'RESET') {
    return [];
  }
};

const useStyles = makeStyles(theme => ({
  dialogContent: {
    overflowY: 'scroll'
    , ...theme.scrollbarStyles
  }

  , lottieContainer: {
    display: 'flex'
    , flexDirection: 'column'
    , justifyContent: 'flex-start'
    , alignItems: 'center'
    , gap: '1rem'
  }

  , lottieContainerMessage: {
    fontSize: '1.75rem'
    , fontWeight: 'bold'
    , textAlign: 'center'
  }

  , card: {
    borderRadius: '20px'
    , padding: '10px'
    , marginBottom: '5px'
  }

  , cardActionArea: {
    display: 'flex'
    , flexDirection: 'row'
    , alignItems: 'center'
    , justifyContent: 'flex-start'
    , borderRadius: '20px'
    , padding: '5px'
  }

  , cardContent: {
    overflow: 'hidden'
  }

  , textName: {
    fontSize: '16px'
    , fontWeight: 'bold'
  }
  
  , textComponent: {
    fontSize: '16px'
    , overflow: 'hidden'
    , whiteSpace: 'nowrap'
    , textOverflow: 'ellipsis'
  }

  , textCategory: {
    fontSize: '14px'
    , color: '#959EAC'
  }

  , floatingButton: {
    transition: 'transform 0.3s'
    , '&:hover': { transform: 'translateY(-5px)' }
  }

  //  ---- Sub Modal ----
  , subModalContainer: {
    display: 'flex'
    , flexDirection: 'column'
    , alignItems: 'flex-start'
    , justifyContent: 'center'
    , gap: '1.00em'
  }

  , subModalAccordionSummary: {
    '& .MuiSvgIcon-root': { color: theme.palette.text.primary }
    , '& .MuiInputBase-input.Mui-disabled': { color: theme.palette.text.primary, filter: 'brightness(0.75)' }
    , '& .MuiInputLabel-root.Mui-disabled': { color: '#606163' }
  }
}));

const TemplateSendingModal = ({ open, onClose, contactId, whatsappId, ticketId }) => {
  //  ****************
  //  ** Attributes **
  //  ****************
  const classes = useStyles();

  const INITIAL_TEMPLATE_PARAMETERS = { header: {}, body: {} };

  const [isLoading, setIsLoading] = useState(false);
  const [templates, dispatch] = useReducer(reducer, []);
  const [isContactPreferenceEnabled, setIsContactPreferenceEnabled] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState('');
  const [hasMore, setHasMore] = useState(false);

  const [isVariablesModalOpen, setIsVariablesModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  const [templateHeaderParameters, setTemplateHeaderParameters] = useState(null);
  const [templateBodyParameters, setTemplateBodyParameters] = useState(null);
  const [templateParameters, setTemplateParameters] = useState(INITIAL_TEMPLATE_PARAMETERS);

  //  *****************
  //  ** Use Effects **
  //  *****************
  useEffect(() => {
    if (!open) return;
    dispatch({ type: 'RESET' });
    setIsContactPreferenceEnabled(false);
    setPageNumber(1);
  }, [open, searchParam]);

  useEffect(() => {
    if (!open) return;

    setIsLoading(true);

    const delayDebounceFn = setTimeout(() => {
      const fetchTemplates = async () => {
        try {
          const { data } = await api.get('officialApi/templateSending', { params: { searchParam, pageNumber, contactId, whatsappId } });
          dispatch({ type: 'LOAD_TEMPLATES', payload: data.templates });
          setIsContactPreferenceEnabled(data.isContactPreferenceEnabled);
          setHasMore(data.hasMore);
        }
        catch (exception) {
          console.log('Template Sending Modal Use-Effect Exception:', exception);
          toastError(exception);
        }
        finally {
          setIsLoading(false);
        }
      };

      fetchTemplates();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [open, searchParam, pageNumber, contactId, whatsappId]);

  //  *************
  //  ** Methods **
  //  *************
  const handleClose = () => {
    handleCloseVariablesModal();
    onClose();

    setTimeout(() => {
      dispatch({ type: 'RESET' });
      setIsContactPreferenceEnabled(false);
      setPageNumber(1);
      setSearchParam('');
      setIsLoading(false);
      setHasMore(false);
      setSelectedTemplate(null);
    }, 500);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleScroll = (event) => {
    if (!hasMore || isLoading) return;
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) setPageNumber(previousPageNumber => previousPageNumber + 1);
  };

  const handleOpenVariablesModal = (template) => {
    setTemplateHeaderParameters(template.parameters.header.length);
    setTemplateBodyParameters(template.parameters.body.length);
    setSelectedTemplate(template);
    setIsVariablesModalOpen(true);
  };

  const handleOnChangeTemplateParameter = (event, componentType, parameter) => {
    setTemplateParameters(previousValue => ({
      ...previousValue
      , [componentType]: { ...previousValue[componentType], [parameter]: event.target.value }
    }));
  };

  const handleCloseVariablesModal = () => {
    setIsVariablesModalOpen(false);
    setSelectedTemplate(null);
    setTemplateHeaderParameters(null);
    setTemplateBodyParameters(null);
    setTemplateHeaderParameters(null);
    setTemplateBodyParameters(null);
    setTemplateParameters(INITIAL_TEMPLATE_PARAMETERS);
  };

  const validateTemplateParameters = () => {
    if (templateHeaderParameters > 0 && Object.keys(templateParameters.header).length !== templateHeaderParameters) {
      return false;
    }

    if (templateBodyParameters > 0 && Object.keys(templateParameters.body).length !== templateBodyParameters) {
      return false;
    }

    return true;
  };

  const handleSendTemplate = async () => {
    setIsLoading(true);

    const areParametersValid = validateTemplateParameters();

    if (!areParametersValid) {
      toast.info(i18n.t('templateSendingModal.toasts.missingParameters'));
      setIsLoading(false);
      return;
    }

    try {
      await api.post(
        `/officialApi/templateSending/${selectedTemplate.templateUuid}`
        , { parameters: templateParameters, ticketId, parameterFormat: selectedTemplate.parameter_format }
      );
      
      handleClose();
    }
    catch (exception) {
      console.log('handleSendTemplate exception:', exception);
      toastError(exception);
    }
    finally {
      setIsLoading(false);
    }
  };

  //  *************
  //  ** Renders **
  //  *************
  const renderContactPreferenceMessage = () => {
    return (
      <React.Fragment>
        <Typography gutterBottom component='h3' className={classes.textCategory}>
          <sup>*</sup>{i18n.t('templateSendingModal.messages.contactPreference')}
        </Typography>
        <Divider />
        <Divider />
      </React.Fragment>
    );
  };

  const renderVariablesModal = () => {
    const renderParametersAccordion = (componentType, parameters) => {
      return (
        <Accordion expanded>
          <AccordionSummary className={classes.subModalAccordionSummary} expandIcon={<ExpandMoreIcon />}>
            {i18n.t(`templateSendingModal.variablesModal.components.${componentType}`)}
          </AccordionSummary>

          <AccordionDetails className={classes.subModalContainer}>
            {parameters.map(parameter => (
              <TextField
                name={`${componentType}-${parameter}`}
                onChange={(event) => handleOnChangeTemplateParameter(event, componentType, parameter)}
                placeholder={i18n.t('templateSendingModal.variablesModal.fields.textPlaceholder')}
                type='text'
                label={parameter}
                InputProps={{ startAdornment: (<InputAdornment position='start'><ViewHeadlineOutlinedIcon style={{ color: 'gray' }} /></InputAdornment>) }}
                disabled={isLoading}
                fullWidth
                required
              />
            ))}
          </AccordionDetails>
        </Accordion>
      );
    };

    const renderComponents = () => {
      if (!selectedTemplate) return (<React.Fragment></React.Fragment>);

      const textHeader = selectedTemplate.components_json.find(component => component.type === 'HEADER')?.text;
      const textBody = selectedTemplate.components_json.find(component => component.type === 'BODY')?.text;
      const textFooter = selectedTemplate.components_json.find(component => component.type === 'FOOTER')?.text;

      const containsHeaderParameters = templateHeaderParameters > 0;
      const containsBodyParameters = templateBodyParameters > 0;
      const containParameters = containsHeaderParameters || containsBodyParameters;

      return (
        <Container>
          <Container className={classes.subModalContainer}>
            <Typography variant='h6' gutterBottom>
              <b>{i18n.t('templateSendingModal.variablesModal.sections.preview')}</b>
            </Typography>

            {textHeader && (
              <Typography><b>{i18n.t('templateSendingModal.variablesModal.components.header')}:</b> {textHeader}</Typography>
            )}

            {textBody && (
              <Typography><b>{i18n.t('templateSendingModal.variablesModal.components.body')}:</b> {textBody}</Typography>
            )}

            {textFooter && (
              <Typography><b>{i18n.t('templateSendingModal.variablesModal.components.footer')}:</b> {textFooter}</Typography>
            )}
          </Container>
            
          {containParameters && (
            <>
              <br />
              <Divider />
              <Divider />
              <Divider />
              <br />

              <Container>
                <Typography variant='h6' gutterBottom>
                  <b>{i18n.t('templateSendingModal.variablesModal.sections.variables')}</b>
                </Typography>

                {containsHeaderParameters && renderParametersAccordion('header', selectedTemplate.parameters.header)}
                {containsBodyParameters && renderParametersAccordion('body', selectedTemplate.parameters.body)}
              </Container>
            </>
          )}
        </Container>
      );
    };

    return (
      <Dialog open={isVariablesModalOpen} onClose={handleCloseVariablesModal} maxWidth='sm' scroll='paper' fullWidth>
        <DialogTitle>
          {i18n.t('templateSendingModal.title')}
        </DialogTitle>

        <DialogContent dividers>
          {renderComponents()}
        </DialogContent>

        <DialogActions>
          <Button className={classes.floatingButton} color='inherit' variant='outlined' onClick={handleCloseVariablesModal}>
            {i18n.t('templateSendingModal.buttons.cancel')}
          </Button>

          <Button className={classes.floatingButton} color='primary' variant='contained' onClick={handleSendTemplate} disabled={isLoading}>
            {i18n.t('templateSendingModal.buttons.send')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderTemplates = () => {
    if (templates.length === 0 && !isLoading) {
      return (
        <div className={classes.lottieContainer}>
          {isContactPreferenceEnabled && renderContactPreferenceMessage()}
          <Lottie options={{ ...LottieUtils.ANIMATION_PROPERTIES, animationData: NotFoundLottie }} isClickToPauseDisabled={true} height={'20rem'} />
          <span className={classes.lottieContainerMessage}>{i18n.t('templateSendingModal.messages.noTemplateFound')}</span>
        </div>
      );
    }

    if (templates.length === 0 && isLoading) {
      return (
        <div className={classes.lottieContainer}>
          {isContactPreferenceEnabled && renderContactPreferenceMessage()}
          <Lottie options={{ ...LottieUtils.ANIMATION_PROPERTIES, animationData: SearchLottie }} isClickToPauseDisabled={true} height={'20rem'} />
        </div>
      );
    }

    const renderTemplateCard = (template) => {
      const bodyText = template.components_json.find(component => component.type === 'BODY')?.text;

      return (
        <Card id={template.templateUuid} className={classes.card} onClick={() => handleOpenVariablesModal(template)}>
          <CardActionArea className={classes.cardActionArea}>
            <CardContent className={classes.cardContent}>
              <Typography gutterBottom component='h3' className={classes.textName}>
                {template.name}
              </Typography>

              {bodyText && (
                <Typography gutterBottom component='h3' className={classes.textComponent}>
                  {bodyText}
                </Typography>
              )}

              <Typography gutterBottom component='h3' className={classes.textCategory}>
                {template.category}
              </Typography>
            </CardContent>
          </CardActionArea>
        </Card>
      );
    };

    return (
      <React.Fragment>
        {isContactPreferenceEnabled && renderContactPreferenceMessage()}
        {templates.map(template => renderTemplateCard(template))}
      </React.Fragment>
    );
  };

  //  ************
  //  ** Return **
  //  ************
  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' scroll='paper' fullWidth>
      {renderVariablesModal()}

      <DialogTitle>
        <span>{i18n.t('templateSendingModal.title')}</span>

        <br /><br />

        <TextField
          placeholder={i18n.t('templateSendingModal.searchPlaceholder')}
          type='search'
          value={searchParam}
          onChange={handleSearch}
          autoFocus
          fullWidth
          InputProps={{ startAdornment: (<InputAdornment position='start'><SearchIcon style={{ color: 'gray' }} /></InputAdornment>) }}
        />
      </DialogTitle>

      <DialogContent className={classes.dialogContent} onScroll={handleScroll} dividers>
        {renderTemplates()}
      </DialogContent>

      <DialogActions>
        <Button className={classes.floatingButton} color='inherit' variant='outlined' onClick={handleClose}>
          {i18n.t('templateSendingModal.buttons.cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateSendingModal;
